import { formatCurrency } from "../../lib/utils";
import { tpaCountLabel } from "./TPACloseInputs";

const dt = (iso) => (iso ? new Date(iso).toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "short" }) : "—");

const Line = ({ label, value, bold, tone = "", testId }) => (
  <div className={`flex justify-between gap-3 py-1 ${bold ? "font-bold border-t mt-1 pt-1.5" : ""}`}>
    <span className={bold ? "" : "text-muted-foreground"}>{label}</span>
    <span className={`font-mono ${tone}`} data-testid={testId}>{value}</span>
  </div>
);

export const CashReportSummary = ({ data }) => {
  const s = data;
  const r = s.report || {};
  const diff = s.difference;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm" data-testid="cash-report-header">
        <div className="rounded-lg border p-2.5"><p className="text-[11px] uppercase text-muted-foreground">Caixa nº</p><p className="font-mono font-semibold truncate">{s.session_id}</p></div>
        <div className="rounded-lg border p-2.5"><p className="text-[11px] uppercase text-muted-foreground">Operador</p><p className="font-semibold truncate">{s.opened_by_name || "—"}</p></div>
        <div className="rounded-lg border p-2.5"><p className="text-[11px] uppercase text-muted-foreground">Abertura</p><p className="font-mono text-xs">{dt(s.opened_at)}</p></div>
        <div className="rounded-lg border p-2.5"><p className="text-[11px] uppercase text-muted-foreground">Fecho</p><p className="font-mono text-xs">{s.closed_at ? dt(s.closed_at) : <span className="text-amber-600">caixa ainda aberta</span>}{s.closed_by_name ? <span className="block text-muted-foreground">{s.closed_by_name}</span> : null}</p></div>
      </div>
      <div className="grid md:grid-cols-2 gap-3 text-sm">
        <div className="rounded-lg border p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Pagamentos por modalidade</p>
          {(r.payments || []).map((p) => (
            <div key={p.method}>
              <Line label={p.label} value={formatCurrency(p.total)} testId={`cash-report-pay-${p.method}`} />
              {p.method === "card" && (r.by_tpa || []).map((g) => (
                <div key={g.tpa_id || "none"} className="flex justify-between gap-3 pl-4 text-xs text-muted-foreground" data-testid={`cash-report-tpa-${g.tpa_id || "none"}`}>
                  <span>› {g.name} ({g.count})</span><span className="font-mono">{formatCurrency(g.total)}</span>
                </div>
              ))}
            </div>
          ))}
          {!r.payments?.length && <p className="text-xs text-muted-foreground py-1">Sem vendas</p>}
          <Line label="Total de vendas" value={formatCurrency(s.total_sales)} bold testId="cash-report-sales-total" />
          <Line label="Outras entradas / Saídas" value={`+${formatCurrency(s.total_entries)} / ${s.total_exits ? "−" : ""}${formatCurrency(s.total_exits)}`} />
        </div>
        <div className="rounded-lg border p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Conferência de numerário</p>
          <Line label="Fundo inicial" value={formatCurrency(s.opening_balance)} />
          <Line label="+ Vendas em numerário" value={formatCurrency(s.total_cash)} />
          <Line label="+ Entradas / − Saídas (numerário)" value={`${formatCurrency(s.total_cash_entries)} / ${s.total_cash_exits ? "−" : ""}${formatCurrency(s.total_cash_exits)}`} />
          <Line label="= Valor esperado em caixa" value={formatCurrency(s.expected_cash)} bold testId="cash-report-expected" />
          {s.closing_balance != null && <>
            <Line label="Valor contado" value={formatCurrency(s.closing_balance)} testId="cash-report-counted" />
            <Line label="Diferença" value={`${diff > 0 ? "+" : ""}${formatCurrency(diff)}`} bold tone={diff < 0 ? "text-red-600" : diff > 0 ? "text-emerald-600" : ""} testId="cash-report-difference" />
          </>}
        </div>
      </div>
      {(s.tpa_closings?.length || s.tpa_expected?.length) ? (
        <div className="rounded-lg border p-3 text-sm" data-testid="cash-report-tpa-check">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Conferência de TPA — sistema vs. fecho do terminal</p>
          <p className="text-[11px] text-muted-foreground mb-1.5">O talão de fecho de cada TPA inclui as vendas e também os recebimentos de clientes / cartões pagos com cartão nesse terminal.</p>
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 text-xs">
            <span className="text-muted-foreground">TPA</span><span className="text-right text-muted-foreground">Sistema</span><span className="text-right text-muted-foreground">Fecho TPA</span><span className="text-right text-muted-foreground">Dif.</span>
            {(s.tpa_closings || s.tpa_expected).map((t) => (
              <div key={t.tpa_id || "none"} className={`contents ${!t.tpa_id ? "text-amber-600" : ""}`} data-testid={`cash-report-tpa-check-${t.tpa_id || "none"}`}>
                <span className="font-medium truncate">{t.name} <span className="text-muted-foreground">({tpaCountLabel(t)})</span></span>
                <span className="font-mono text-right">{formatCurrency(t.expected)}</span>
                <span className="font-mono text-right">{t.counted != null ? formatCurrency(t.counted) : "—"}</span>
                <span className={`font-mono text-right font-semibold ${t.difference < 0 ? "text-red-600" : t.difference > 0 ? "text-emerald-600" : ""}`}>{t.difference != null ? `${t.difference > 0 ? "+" : ""}${formatCurrency(t.difference)}` : "—"}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
      {(s.opening_notes || s.closing_notes) && (
        <p className="text-xs text-muted-foreground rounded-md border p-2">{s.opening_notes && <>Abertura: {s.opening_notes}. </>}{s.closing_notes && <>Fecho: {s.closing_notes}</>}</p>
      )}
    </div>
  );
};
